import { useFrontendTool as useCopilotKitFrontendTool } from '@copilotkit/react-core/v2'
import { type ReactElement } from 'react'
import { type CopilotAgentId, DATA_AGENT_RUNTIME_IDS } from '../copilotSurfaces'

/**
 * Backend ADK `LongRunningFunctionTool` names.
 *
 * `ag_ui_adk` emits TOOL_CALL_* for these but skips TOOL_CALL_RESULT.
 * Named CopilotKit stubs (`available: false`, `followUp: false`) prevent the
 * `*` wildcard from erroring and auto-following-up. Plan approval continues via
 * `sendUserMessage`, not tool resume.
 *
 * Scoped to the data agents. These LROs are data-agent tools, not sidenav page tools.
 */
const BACKEND_LONG_RUNNING_TOOL_NAMES = ['submit_plan_for_approval', 'submit_instructions_update'] as const

const BackendLongRunningToolStub = ({
  name,
  agentId
}: {
  name: (typeof BACKEND_LONG_RUNNING_TOOL_NAMES)[number]
  agentId: CopilotAgentId
}): null => {
  useCopilotKitFrontendTool({
    name,
    agentId,
    available: false,
    followUp: false,
    handler: async (args: Record<string, unknown>) => ({
      status: 'pending',
      ...args,
      message: 'Awaiting human review.'
    })
  })
  return null
}

/**
 * Registers named CopilotKit frontend stubs for backend ADK long-running tools.
 * Add new LRO tool names to {@link BACKEND_LONG_RUNNING_TOOL_NAMES} above.
 */
export const BackendLongRunningToolStubs = (): ReactElement => (
  <>
    {DATA_AGENT_RUNTIME_IDS.flatMap((agentId) =>
      BACKEND_LONG_RUNNING_TOOL_NAMES.map((name) => (
        <BackendLongRunningToolStub key={`${agentId}:${name}`} name={name} agentId={agentId} />
      ))
    )}
  </>
)
